import React, { useState, useEffect, useCallback } from "react";
import api from "../services/api";

const GOOD_GMQ = 0.75;

const Row = ({ label, value, last }) => (
  <div
    className={
      "flex items-center justify-between" + (last ? "" : " border-b border-stone-300 dark:border-slate-600")
    }
  >
    <span className="py-2 text-sm font-medium text-stone-600 dark:text-slate-300">
      {label}
    </span>
    <span className="mr-3 text-sm text-stone-400">{value}</span>
  </div>
);

/** A daily gain, coloured against the 0.75 kg/day target. */
const Gmq = ({ value }) => (
  <span
    className={
      "mr-3 rounded-full px-2 text-sm font-medium " +
      (value > GOOD_GMQ
        ? "bg-emerald-100 text-emerald-500 dark:bg-emerald-900 dark:text-emerald-300"
        : "bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300")
    }
  >
    {value} Kg/J
  </span>
);

/**
 * One weighing, with how it compares to the previous one and to birth.
 *
 * The calf sheet opens this in a dialog on desktop and the /poids route shows it
 * as a page on mobile — same component either way.
 */
function WeightDetail({ weightId, onDeleted }) {
  const [weight, setWeight] = useState(null);

  const getWeight = useCallback(() => {
    api
      .get("/weight/" + weightId)
      .then((response) => setWeight(response.data))
      .catch((error) => {
        console.error("There was an error getting the weight!", error);
      });
  }, [weightId]);

  useEffect(() => {
    getWeight();
  }, [getWeight]);

  const handleDelete = () => {
    api
      .delete("/weight/" + weightId)
      .then(() => onDeleted())
      .catch((error) => {
        console.error("There was an error deleting the weight!", error);
      });
  };

  if (!weight) {
    return <div></div>;
  }

  const formatDate = (value) =>
    value
      ? new Date(value).toLocaleString("default", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
      : "—";

  return (
    <div className="flex flex-col">
      <div className="mx-3 rounded-lg bg-white dark:bg-slate-800">
        <div className="ml-3 flex flex-col">
          <Row label="Poids" value={`${weight.weight} kg`} />
          <Row label="Date de la pesée" value={formatDate(weight.date)} last />
        </div>
      </div>

      <div className="mx-3 mt-3 rounded-lg bg-stone-100 pl-3 dark:bg-slate-700/50">
        <Row label="GMQ avec le poids précédent" value={<Gmq value={weight.gmq_interval} />} />
        <Row label="Poids précédent" value={`${weight.previous_weight} Kg`} />
        <Row label="GMQ avec le poids de naissance" value={<Gmq value={weight.gmq_birth} />} />
        <Row label="Poids à la naissance" value={`${weight.birth_weight} Kg`} last />
      </div>

      <div className="mt-3 px-3 md:self-end">
        <button
          className="w-full rounded-lg border border-stone-200 bg-white py-2 font-semibold text-red-500 shadow transition-colors duration-200 md:w-fit md:bg-red-600 md:px-3 md:text-white md:hover:bg-red-700 dark:border-red-900 dark:bg-red-600 dark:text-white"
          onClick={handleDelete}
        >
          Supprimer
        </button>
      </div>
    </div>
  );
}

export default WeightDetail;
